import { trackEvent } from "@/engine/diagnostics/analytics";
import type { EntryStage } from "./messageContracts";
export type { EntryStage } from "./messageContracts";

/**
 * How long the way into a room actually took, step by step.
 *
 * "It felt slow" is not a measurement, and the walk in is made of four waits
 * that look identical from outside: the press, the account check, the channel,
 * and the first audio. Each one is stamped here as it passes, so the screen can
 * show where the time is going while it is going, and PostHog can say afterwards
 * which step it was.
 *
 * The clock is injectable and the store is plain, so this is unit testable
 * without a browser.
 */

/** The order the stages are expected in, and the words the screen uses. */
/**
 * The stages in the order a visitor passes them. What each is called is in the
 * catalogue under `entryStage.<stage>`: no layer here renders, and a name a
 * visitor reads has to be translatable.
 */
export const ENTRY_STAGES: EntryStage[] = [
  "session-checked",
  "channel-connected",
  "audio-started",
];

export interface EntryTiming {
  kind: "create" | "join";
  /** Milliseconds since the press, per stage that has passed. */
  marks: Partial<Record<EntryStage, number>>;
  /** True once audio is live — the walk in is over. */
  done: boolean;
}

let clock: () => number = () => Date.now();
let startedAt = 0;
let snapshot: EntryTiming | null = null;
const listeners = new Set<() => void>();

/** Test seam: the clock the marks are measured against. */
export function setEntryTimingClock(next: () => number) {
  clock = next;
}

export function subscribeEntryTiming(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getEntryTiming(): EntryTiming | null {
  return snapshot;
}

function publish(next: EntryTiming | null) {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/** The press. Everything after this is measured from here. */
export function startEntryTiming(kind: "create" | "join") {
  startedAt = clock();
  publish({ kind, marks: { "entry-requested": 0 }, done: false });
  trackEvent("room-entry-stage-recorded", {
    stage: "entry-requested",
    ms: 0,
    kind,
  });
}

/**
 * A stage has passed. Marking one twice is ignored: a rejoin re-marks nothing,
 * it starts over.
 */
export function markEntryStage(stage: EntryStage): number | null {
  if (!snapshot || snapshot.marks[stage] !== undefined) return null;
  const ms = clock() - startedAt;
  publish({
    ...snapshot,
    marks: { ...snapshot.marks, [stage]: ms },
    done: snapshot.done || stage === "audio-started",
  });
  trackEvent("room-entry-stage-recorded", { stage, ms, kind: snapshot.kind });
  return ms;
}

/**
 * The stage still being waited on, or `undefined` once the walk in is over.
 *
 * One implementation rather than two: the room's pill and the entry card both
 * draw the same three steps, and "which one are we on" is the sort of one-liner
 * that gets copied and then quietly disagrees.
 */
export function currentEntryStage(
  marks: EntryTiming["marks"],
): EntryStage | undefined {
  return ENTRY_STAGES.find((stage) => marks[stage] === undefined);
}

/**
 * How long a step may take before the screen admits it is slow, and offers a
 * way out.
 *
 * Four seconds, from the entry book's timings. By the fourth second the visitor
 * is already wondering whether something broke, and a screen that says nothing
 * is leaving them to wonder. Measured per step rather than over the whole walk:
 * three ordinary steps of a second and a half each are not a stall.
 */
export const SLOW_STAGE_MS = 4000;

/**
 * How long an entry gets before it starts explaining itself.
 *
 * One second, from the entry book's timings, and one constant for both things
 * that wait it out: the elapsed counter beside the steps, and the panel that
 * replaces the door. They are the same rule for the same reason — an entry that
 * is over before this has nothing to explain, and a panel that appeared and
 * vanished inside a fraction of a second reads as a fault rather than as
 * progress.
 */
export const EXPLAIN_AFTER_MS = 1000;

/**
 * How long the walk in has been standing on the step it is on.
 *
 * The marks are milliseconds since the press, so the step's own age is the
 * elapsed time less the last mark that passed — and the press itself is zero,
 * which is what the first step is measured from.
 */
export function msOnCurrentStage(
  marks: EntryTiming["marks"],
  elapsed: number,
): number {
  const passed = ENTRY_STAGES.map((stage) => marks[stage] ?? 0);
  return Math.max(0, elapsed - Math.max(0, ...passed));
}

/** Whether the step being waited on has taken long enough to say so. */
export function stageIsSlow(
  marks: EntryTiming["marks"],
  elapsed: number,
): boolean {
  return msOnCurrentStage(marks, elapsed) >= SLOW_STAGE_MS;
}

/**
 * How long the room stays behind the loader at most, counted from the moment
 * it is mounted behind it.
 *
 * A step that never comes — a voice that cannot start, a stage whose code is
 * still on its way — must not keep the visitor out of a room they are already
 * standing in. Ten seconds is past every ordinary entry the e2e suite sees, and
 * short of the moment a visitor gives up on a screen that is still moving.
 */
export const ENTRY_CEILING_MS = 10_000;

/**
 * How many of the loader's steps are done, counted from the first: a later step
 * that finished early waits for the one before it, so the numbers only ever
 * climb one at a time.
 *
 * Each step is one stage of the walk in, and the first is also the room's own
 * stage — its code, its scene and its figures — being ready to be looked at.
 * A room drawn before that is the half-built frame this loader is for.
 */
export function entryStepsDone(
  marks: EntryTiming["marks"],
  stageReady: boolean,
): number {
  const done = ENTRY_STAGES.map(
    (stage, index) => marks[stage] !== undefined && (index > 0 || stageReady),
  );
  const first = done.indexOf(false);
  return first === -1 ? done.length : first;
}

/** Leaving the room puts the measurement away with it. */
export function resetEntryTiming() {
  startedAt = 0;
  publish(null);
}
